import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateShort, dateTime, money, money0, pct } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { InvoiceDoc, type Invoice } from '../components/InvoiceDoc';
import {
  ConfirmDialog,
  Drawer,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface Room {
  id: string;
  number: string;
  floor: number;
  status: 'VACANT' | 'OCCUPIED' | 'DIRTY' | 'OUT_OF_ORDER';
  roomType: string;
  tariff: number;
  maxOccupancy: number;
  amenities: string[];
  gstRate: number;
  openTasks: number;
  stay: { id: string; folioNo: string; guestName: string; checkInAt: string; nights: number; pax: number } | null;
}

interface RoomsSummary {
  total: number;
  occupied: number;
  vacant: number;
  dirty: number;
  outOfOrder: number;
  occupancyPct: number;
  adr: number;
  revpar: number;
}

interface FolioLine {
  description: string;
  hsn: string;
  quantity: number;
  unitPrice: number;
  gstRate: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  sourceInvoiceNumber: string | null;
}

interface Folio {
  stay: {
    id: string;
    folioNo: string;
    status: string;
    roomNumber: string;
    roomType: string;
    guestName: string;
    phone: string | null;
    gstin: string | null;
    checkInAt: string;
    nights: number;
    tariff: number;
    adults: number;
    children: number;
  };
  lines: FolioLine[];
  totals: { subtotal: number; cgst: number; sgst: number; igst: number; roundOff: number; total: number };
  advance: number;
  balance: number;
}

export function FrontDeskPage() {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();

  const [checkInRoom, setCheckInRoom] = useState<Room | null>(null);
  const [statusRoom, setStatusRoom] = useState<Room | null>(null);
  const [folioStayId, setFolioStayId] = useState<string | null>(null);

  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/api/rooms') });
  const summary = useQuery({ queryKey: ['rooms', 'summary'], queryFn: () => api.get<RoomsSummary>('/api/rooms/summary') });

  const setStatus = useMutation({
    mutationFn: (input: { roomId: string; status: string }) =>
      api.post(`/api/rooms/${input.roomId}/status`, { status: input.status }),
    onSuccess: () => {
      push('Room updated');
      setStatusRoom(null);
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
    },
    onError: fail,
  });

  if (rooms.isLoading) return <Loading label="Loading the rack…" />;
  if (rooms.error) return <ErrorState error={rooms.error} onRetry={() => void rooms.refetch()} />;

  const stats = summary.data;

  return (
    <div className="stack">
      <PageHeader title="Front Desk" subtitle="Room rack, check-in, folio and check-out" />

      {stats ? (
        <div className="kpis">
          <Kpi label="Occupancy" value={pct(stats.occupancyPct)} foot={`${stats.occupied} of ${stats.total} rooms`} tone="blue" />
          <Kpi label="ADR" value={money0(stats.adr)} foot="Average daily rate, in-house" />
          <Kpi label="RevPAR" value={money0(stats.revpar)} foot="Revenue per available room" />
          <Kpi
            label="Housekeeping"
            value={stats.dirty}
            foot={`${stats.vacant} ready · ${stats.outOfOrder} out of order`}
            tone={stats.dirty > 0 ? 'amber' : ''}
          />
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>Room rack</h2>
          <div className="legend">
            <span>
              <i style={{ background: 'var(--ok)' }} />
              Vacant
            </span>
            <span>
              <i style={{ background: 'var(--info)' }} />
              Occupied
            </span>
            <span>
              <i style={{ background: 'var(--accent)' }} />
              Needs cleaning
            </span>
            <span>
              <i style={{ background: 'var(--danger)' }} />
              Out of order
            </span>
          </div>
        </div>
        <div className="card-body">
          <div className="rack">
            {rooms.data!.map((room) => (
              <button
                key={room.id}
                className={`room ${room.status}`}
                onClick={() => {
                  if (room.stay) setFolioStayId(room.stay.id);
                  else if (room.status === 'VACANT' && can('rooms:checkin')) setCheckInRoom(room);
                  else setStatusRoom(room);
                }}
              >
                <div className="row between">
                  <span className="r-no">{room.number}</span>
                  <StatusChip status={room.status} />
                </div>
                <div className="r-type">
                  {room.roomType} · {money0(room.tariff)}/night · GST {room.gstRate}%
                </div>
                <div className="r-guest">{room.stay?.guestName ?? '—'}</div>
                <div className="r-foot">
                  <span className="xs muted">
                    {room.stay ? `${room.stay.nights} night(s) · ${room.stay.pax} pax` : `Sleeps ${room.maxOccupancy}`}
                  </span>
                  <span className="xs b">{room.stay ? dateShort(room.stay.checkInAt) : ''}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {checkInRoom ? (
        <CheckInModal
          room={checkInRoom}
          onClose={() => setCheckInRoom(null)}
          onDone={(stayId) => {
            setCheckInRoom(null);
            setFolioStayId(stayId);
            void queryClient.invalidateQueries({ queryKey: ['rooms'] });
          }}
        />
      ) : null}

      {statusRoom ? (
        <Modal
          title={`Room ${statusRoom.number}`}
          size="narrow"
          onClose={() => setStatusRoom(null)}
          footer={
            <>
              {statusRoom.status !== 'VACANT' ? (
                <button
                  className="btn primary"
                  onClick={() => setStatus.mutate({ roomId: statusRoom.id, status: 'VACANT' })}
                >
                  {statusRoom.status === 'DIRTY' ? 'Mark cleaned' : 'Return to service'}
                </button>
              ) : null}
              {statusRoom.status !== 'OUT_OF_ORDER' ? (
                <button
                  className="btn danger"
                  onClick={() => setStatus.mutate({ roomId: statusRoom.id, status: 'OUT_OF_ORDER' })}
                >
                  Mark out of order
                </button>
              ) : null}
              <button className="btn" onClick={() => setStatusRoom(null)}>
                Close
              </button>
            </>
          }
        >
          <div className="col gap-6">
            <div className="stat-line">
              <span className="dim">Type</span>
              <b>{statusRoom.roomType}</b>
            </div>
            <div className="stat-line">
              <span className="dim">Tariff</span>
              <b>{money0(statusRoom.tariff)}</b>
            </div>
            <div className="stat-line">
              <span className="dim">Status</span>
              <StatusChip status={statusRoom.status} />
            </div>
            <div className="stat-line">
              <span className="dim">Amenities</span>
              <span className="small right">{statusRoom.amenities.join(', ')}</span>
            </div>
          </div>
        </Modal>
      ) : null}

      {folioStayId ? <FolioDrawer stayId={folioStayId} onClose={() => setFolioStayId(null)} /> : null}
    </div>
  );
}

function CheckInModal({
  room,
  onClose,
  onDone,
}: {
  room: Room;
  onClose: () => void;
  onDone: (stayId: string) => void;
}) {
  const { fail, push } = useToast();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    idType: 'AADHAAR',
    idNumber: '',
    gstin: '',
    nights: 1,
    adults: 2,
    children: 0,
    tariff: room.tariff,
    advance: 0,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const checkIn = useMutation({
    mutationFn: () =>
      api.post<Folio>(`/api/rooms/${room.id}/check-in`, {
        guest: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          idType: form.idType,
          idNumber: form.idNumber,
          gstin: form.gstin,
        },
        nights: form.nights,
        adults: form.adults,
        children: form.children,
        tariff: form.tariff,
        advance: form.advance,
      }),
    onSuccess: (folio) => {
      push(`${form.name} checked into ${room.number} · ${folio.stay.folioNo}`, 'ok');
      onDone(folio.stay.id);
    },
    onError: fail,
  });

  const fieldErrors = checkIn.error instanceof Error && 'fieldErrors' in checkIn.error
    ? (checkIn.error as { fieldErrors: Record<string, string> }).fieldErrors
    : {};

  return (
    <Modal
      title={`Check in · Room ${room.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || checkIn.isPending}
            onClick={() => checkIn.mutate()}
          >
            {checkIn.isPending ? 'Checking in…' : 'Check in'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="row gap-6 wrap">
          <span className="chip brand">{room.roomType}</span>
          <span className="chip">{money0(room.tariff)} / night</span>
          <span className="chip">Sleeps {room.maxOccupancy}</span>
          <span className="chip info">GST {room.gstRate}%</span>
        </div>

        <div className="form-grid">
          <TextInput
            className="span2"
            label="Guest name *"
            autoFocus
            value={form.name}
            error={fieldErrors['guest.name']}
            onChange={(e) => set('name', e.target.value)}
            placeholder="As printed on the ID"
          />
          <TextInput label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 …" />
          <TextInput label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          <Select label="ID type" value={form.idType} onChange={(e) => set('idType', e.target.value)}>
            <option value="AADHAAR">Aadhaar</option>
            <option value="PASSPORT">Passport</option>
            <option value="DRIVING_LICENCE">Driving licence</option>
            <option value="VOTER_ID">Voter ID</option>
          </Select>
          <TextInput label="ID number" value={form.idNumber} onChange={(e) => set('idNumber', e.target.value)} />
          <TextInput
            className="span2"
            label="Company GSTIN (optional)"
            value={form.gstin}
            error={fieldErrors['guest.gstin']}
            hint="A GSTIN from another state switches the folio to IGST"
            onChange={(e) => set('gstin', e.target.value.toUpperCase())}
          />
        </div>

        <div className="form-grid three">
          <TextInput label="Nights" type="number" min={1} value={form.nights} onChange={(e) => set('nights', Number(e.target.value) || 1)} />
          <TextInput label="Adults" type="number" min={1} value={form.adults} onChange={(e) => set('adults', Number(e.target.value) || 1)} />
          <TextInput label="Children" type="number" min={0} value={form.children} onChange={(e) => set('children', Number(e.target.value) || 0)} />
          <TextInput label="Tariff / night" type="number" value={form.tariff} onChange={(e) => set('tariff', Number(e.target.value) || 0)} />
          <TextInput label="Advance collected" type="number" value={form.advance} onChange={(e) => set('advance', Number(e.target.value) || 0)} />
        </div>
      </div>
    </Modal>
  );
}

function FolioDrawer({ stayId, onClose }: { stayId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [receipt, setReceipt] = useState<Invoice | null>(null);
  const [amend, setAmend] = useState<{ nights: number; tariff: number; advance: number } | null>(null);

  const folio = useQuery({ queryKey: ['folio', stayId], queryFn: () => api.get<Folio>(`/api/stays/${stayId}/folio`) });

  const update = useMutation({
    mutationFn: (input: { nights: number; tariff: number; advance: number }) => api.patch<Folio>(`/api/stays/${stayId}`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['folio', stayId], data);
      setAmend(null);
      push('Folio updated');
    },
    onError: fail,
  });

  const checkOut = useMutation({
    mutationFn: () => api.post<{ invoice: Invoice; balanceSettled: number }>(`/api/stays/${stayId}/check-out`, { mode: 'CARD' }),
    onSuccess: (result) => {
      push(`Checked out · ${result.invoice.number}`, 'ok');
      setConfirming(false);
      setReceipt(result.invoice);
      void queryClient.invalidateQueries({ queryKey: ['rooms'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      setConfirming(false);
      fail(error);
    },
  });

  const openRoomService = useMutation({
    mutationFn: () => api.post('/api/tickets', { type: 'ROOM_SERVICE', stayId, covers: 1 }),
    onSuccess: () => {
      push('Room-service ticket opened');
      navigate('/pos');
    },
    onError: fail,
  });

  const data = folio.data;

  return (
    <>
      <Drawer
        title={data ? `Folio ${data.stay.folioNo} · Room ${data.stay.roomNumber}` : 'Folio'}
        size="wide"
        onClose={onClose}
        footer={
          data && data.stay.status === 'IN_HOUSE' ? (
            <>
              <button className="btn" onClick={onClose}>
                Close
              </button>
              <button className="btn" onClick={() => openRoomService.mutate()}>
                Room-service ticket
              </button>
              {can('rooms:checkout') ? (
                <button className="btn primary" onClick={() => setConfirming(true)}>
                  Check out &amp; settle
                </button>
              ) : null}
            </>
          ) : (
            <button className="btn" onClick={onClose}>
              Close
            </button>
          )
        }
      >
        {folio.isLoading || !data ? (
          <Loading />
        ) : (
          <div className="stack">
            <div className="card">
              <div className="card-body row between wrap gap-16">
                <div>
                  <h2>{data.stay.guestName}</h2>
                  <div className="small muted">
                    {data.stay.phone ?? 'No phone'} {data.stay.gstin ? `· GSTIN ${data.stay.gstin}` : ''}
                  </div>
                  <div className="small dim" style={{ marginTop: 4 }}>
                    Checked in {dateTime(data.stay.checkInAt)} · {data.stay.nights} night(s) ·{' '}
                    {data.stay.adults + data.stay.children} pax
                  </div>
                </div>
                <div className="right">
                  <div className="muted xs">Balance due</div>
                  <div style={{ fontSize: 24, fontWeight: 750 }}>{money(data.balance)}</div>
                  <div className="xs dim">Advance {money(data.advance)}</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2>Folio charges</h2>
                <span className="chip">{data.lines.length} line(s)</span>
              </div>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th className="num">Qty</th>
                      <th className="num">Rate</th>
                      <th className="num">Taxable</th>
                      <th className="num">GST</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((line, index) => (
                      <tr key={`${line.description}-${index}`}>
                        <td>
                          {line.description}
                          {line.sourceInvoiceNumber ? (
                            <div className="xs muted mono">{line.sourceInvoiceNumber}</div>
                          ) : null}
                        </td>
                        <td className="mono xs">{line.hsn}</td>
                        <td className="num">{line.quantity}</td>
                        <td className="num">{money(line.unitPrice)}</td>
                        <td className="num">{money(line.taxable)}</td>
                        <td className="num dim">
                          {line.gstRate}% · {money(line.cgst + line.sgst + line.igst)}
                        </td>
                        <td className="num b">{money(line.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={6}>Taxable value</td>
                      <td className="num">{money(data.totals.subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan={6}>GST</td>
                      <td className="num">{money(data.totals.cgst + data.totals.sgst + data.totals.igst)}</td>
                    </tr>
                    <tr>
                      <td colSpan={6}>Folio total</td>
                      <td className="num">{money(data.totals.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {data.stay.status === 'IN_HOUSE' && can('rooms:checkin') ? (
              <div className="card">
                <div className="card-head">
                  <h2>Amend stay</h2>
                  {!amend ? (
                    <button
                      className="btn sm"
                      onClick={() =>
                        setAmend({ nights: data.stay.nights, tariff: data.stay.tariff, advance: data.advance })
                      }
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {amend ? (
                  <div className="card-body">
                    <div className="form-grid three">
                      <TextInput
                        label="Nights"
                        type="number"
                        min={1}
                        value={amend.nights}
                        onChange={(e) => setAmend({ ...amend, nights: Number(e.target.value) || 1 })}
                      />
                      <TextInput
                        label="Tariff"
                        type="number"
                        value={amend.tariff}
                        onChange={(e) => setAmend({ ...amend, tariff: Number(e.target.value) || 0 })}
                      />
                      <TextInput
                        label="Advance"
                        type="number"
                        value={amend.advance}
                        onChange={(e) => setAmend({ ...amend, advance: Number(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="row gap-6" style={{ marginTop: 11 }}>
                      <button className="btn primary sm" onClick={() => update.mutate(amend)} disabled={update.isPending}>
                        Save changes
                      </button>
                      <button className="btn sm" onClick={() => setAmend(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </Drawer>

      {confirming && data ? (
        <ConfirmDialog
          title="Check out"
          message={`Settle ${money(data.balance)} for ${data.stay.guestName} and release room ${data.stay.roomNumber}?`}
          confirmLabel="Check out"
          busy={checkOut.isPending}
          onConfirm={() => checkOut.mutate()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}

      {receipt ? (
        <Modal
          title={`Invoice ${receipt.number}`}
          size="wide"
          onClose={() => {
            setReceipt(null);
            onClose();
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setReceipt(null);
                  onClose();
                }}
              >
                Close
              </button>
              <button className="btn primary" onClick={() => window.print()}>
                Print
              </button>
            </>
          }
        >
          <InvoiceDoc invoice={receipt} />
        </Modal>
      ) : null}
    </>
  );
}
